import api from './api';
import toast from '../utils/toast';

/**
 * Geocoding service for postal code lookup
 */
class GeocodingService {
  /**
   * Geocode a postal code using the backend API
   * @param {string} postalCode - The postal code to geocode
   * @param {string} country - Country code (default: 'SG')
   * @returns {Promise<Object|null>} Geocoded data or null if failed
   */
  static async geocodePostalCode(postalCode, country = 'SG') {
    try {
      // Validate input
      if (!postalCode || typeof postalCode !== 'string') {
        throw new Error('Postal code is required and must be a string');
      }

      // Clean the postal code
      const cleanedPostalCode = postalCode.trim().replace(/\s+/g, '');
      
      if (cleanedPostalCode.length === 0) {
        throw new Error('Postal code cannot be empty');
      }

      // Validate Singapore postal code format (6 digits)
      if (country === 'SG' && !/^\d{6}$/.test(cleanedPostalCode)) {
        throw new Error('Singapore postal code must be 6 digits');
      }

      console.log(`🌍 Geocoding postal code: ${cleanedPostalCode} (${country})`);

      // Call the backend geocoding API
      const response = await api.post('/geocode/', {
        postal_code: cleanedPostalCode,
        country: country
      });

      if (response.data.success && response.data.data) {
        const geocodedData = response.data.data;
        
        console.log('✅ Geocoding successful:', geocodedData);
        
        return {
          success: true,
          data: {
            address: geocodedData.address,
            postalCode: geocodedData.postal_code,
            latitude: geocodedData.latitude,
            longitude: geocodedData.longitude,
            building: geocodedData.building,
            roadName: geocodedData.road_name,
            blockNumber: geocodedData.block_number
          }
        };
      } else {
        console.warn('❌ Geocoding failed:', response.data.error);
        return {
          success: false,
          error: response.data.error || 'Unknown geocoding error'
        };
      }

    } catch (error) {
      console.error('❌ Geocoding service error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const serverError = error.response.data?.error || 'Server error during geocoding';
        return {
          success: false,
          error: serverError
        };
      } else if (error.request) {
        // Network error
        return {
          success: false,
          error: 'Network error - please check your connection'
        };
      } else {
        // Client-side error
        return {
          success: false,
          error: error.message || 'Unknown error during geocoding'
        };
      }
    }
  }

  /**
   * Geocode with user feedback (shows toast messages)
   * @param {string} postalCode - The postal code to geocode
   * @param {Function} onSuccess - Callback for successful geocoding
   * @param {Function} onError - Callback for failed geocoding
   * @param {string} country - Country code (default: 'SG')
   */
  static async geocodeWithFeedback(postalCode, onSuccess, onError, country = 'SG') {
    const loadingToast = toast.loading('Looking up postal code...');
    
    try {
      const result = await this.geocodePostalCode(postalCode, country);
      
      toast.dismiss(loadingToast);
      
      if (result.success) {
        toast.success('✅ Address found successfully!');
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(result.data);
        }
        return result.data;
      } else {
        toast.error(`❌ ${result.error}`);
        if (onError && typeof onError === 'function') {
          onError(result.error);
        }
        return null;
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('❌ Failed to lookup postal code');
      if (onError && typeof onError === 'function') {
        onError(error.message);
      }
      return null;
    }
  }

  /**
   * Reverse-geocode WGS84 coordinates to a street-level address suggestion.
   * Silent on failure — callers use this as optional assist only.
   *
   * @param {number} latitude
   * @param {number} longitude
   * @returns {Promise<string|null>} Address string or null
   */
  static async reverseGeocode(latitude, longitude) {
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    try {
      const response = await api.get('/geocode/reverse/', {
        params: { lat, lng },
      });
      const address = response.data?.data?.address;
      return typeof address === 'string' && address.trim() ? address.trim() : null;
    } catch {
      return null;
    }
  }

  /**
   * Validate Singapore postal code format
   * @param {string} postalCode - Postal code to validate
   * @returns {boolean} True if valid format
   */
  static isValidSingaporePostalCode(postalCode) {
    if (!postalCode || typeof postalCode !== 'string') {
      return false;
    }
    
    const cleaned = postalCode.trim().replace(/\s+/g, '');
    return /^\d{6}$/.test(cleaned);
  }

  /**
   * Validate Singapore unit number format
   * @param {string} unitNumber - The unit number to validate
   * @returns {Object} Validation result with isValid boolean and formatted string
   */
  static validateUnitNumber(unitNumber) {
    if (!unitNumber || typeof unitNumber !== 'string') {
      return { isValid: true, formatted: '', message: 'Unit number is optional' };
    }

    const cleaned = unitNumber.trim();
    if (cleaned.length === 0) {
      return { isValid: true, formatted: '', message: 'Unit number is optional' };
    }

    // Common Singapore unit number patterns
    const patterns = [
      /^#\d{2}-\d{2,3}$/,           // #12-34, #01-123
      /^\d{2}-\d{2,3}$/,            // 12-34, 01-123
      /^Unit\s+\d+$/i,              // Unit 5, unit 12
      /^Level\s+\d+$/i,             // Level 3, level 10
      /^Floor\s+\d+$/i,             // Floor 2, floor 15
      /^Blk\s+\d+\s+#\d{2}-\d{2,3}$/i, // Blk 123 #04-56
      /^#\d{2}-\d{2,3}[A-Z]?$/,     // #12-34A, #01-02B
    ];

    const isValid = patterns.some(pattern => pattern.test(cleaned));
    
    if (isValid) {
      return { 
        isValid: true, 
        formatted: cleaned, 
        message: 'Valid unit number format' 
      };
    } else {
      return { 
        isValid: false, 
        formatted: cleaned, 
        message: 'Common formats: #12-34, Unit 5, Level 3, etc.' 
      };
    }
  }

  /**
   * Format postal code with proper spacing
   * @param {string} postalCode - The postal code to format
   * @returns {string} Formatted postal code
   */
  static formatPostalCode(postalCode) {
    if (!postalCode || typeof postalCode !== 'string') {
      return '';
    }

    const cleaned = postalCode.replace(/\D/g, '');
    if (cleaned.length === 6) {
      return `${cleaned.substring(0, 3)} ${cleaned.substring(3)}`;
    }
    return postalCode;
  }

  /**
   * Get distance between two coordinates in kilometers
   * @param {number} lat1 - Latitude of first point
   * @param {number} lng1 - Longitude of first point  
   * @param {number} lat2 - Latitude of second point
   * @param {number} lng2 - Longitude of second point
   * @returns {number} Distance in kilometers
   */
  static calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c;
  }

  /**
   * Convert degrees to radians
   * @param {number} degrees - Degrees to convert
   * @returns {number} Radians
   */
  static toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }
}

export default GeocodingService; 